/* eslint-disable max-len */
import { SyncOutlined, CloseOutlined } from '@ant-design/icons';
import {
  Col,
  DatePicker,
  Form, Input,
  Modal,
  Row,
  Spin,
} from 'antd';
import { patchGenerarDeclaracion, postDeclaracionJuegos, postRechazarDeclaracion } from 'api/recaudacion';
import { Button, Text } from 'components';
import Select from 'components/PaginatedSelect';
import moment from 'moment';
import propTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { actualizacionTotal, getINPC, recargosTotal } from 'utils/autocalculos';
import { DATE_FORMAT } from 'utils/formatters';
import _logger from 'utils/logger';
import { toMoment } from 'utils/normalizers';
import { CURP_PATTERN, RFC_PATTERN } from 'utils/patterns';
import round from 'utils/round';
import ModalConfirm from './ModalConfirm';

const _rules = {
  required: (message) => ({
    required: true,
    message,
  }),
  numeric: [
    {
      validator: async (_, value) => {
        const regex = /^[0-9]+([.][0-9]{1,2})?$/;
        if (!value) return Promise.resolve();
        if (!Number.isInteger(parseFloat(value))) {
          return Promise.reject(new Error('Solo se permiten números enteros positivos.'));
        }
        if (!regex.test(value)) {
          return Promise.reject(new Error('Solo se permiten números positivos.'));
        }
        return Promise.resolve();
      },
    },
  ],
  mayorCero: [
    {
      validator: async (_, value) => {
        if (value <= 0) {
          throw new Error('El valor debe ser mayor a 0.');
        }
      },
    },
  ],
  entero: [
    {
      validator: async (_, value) => {
        const regex = /^[0-9]+$/;
        if (!value) return Promise.resolve();
        if (!regex.test(value)) {
          return Promise.reject(new Error('Solo se permiten números enteros positivos.'));
        }
        return Promise.resolve();
      },
    },
  ],
  rfc: [{ pattern: RFC_PATTERN, message: ' Ingrese un RFC valido.' }],
  curp: [{ pattern: CURP_PATTERN, message: ' Ingrese una CURP valida.' }],
};

const FormJuegos = ({
  padron,
  statusDeclaracion,
  visible,
  setVisibleForm,
  isComplementaria,
  selectedCargos,
  callback,
  callbackCancel,
  declaracionId,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [isZeroState, setIsZeroState] = useState(false);
  const [INPC, setINPC] = useState(0);

  // watchers
  const _impuesto = Form.useWatch('impuesto_determinado', form);
  const _importeDeActualizacion = Form.useWatch('actualizacion', form);
  const _importeRecargos = Form.useWatch('recargos', form);
  const _importeMultas = Form.useWatch('multas', form);
  const _importeTotalDeContribuciones = Form.useWatch('total_de_contribuciones', form);
  const _importePagadoConAnterioridad = Form.useWatch('monto_pagado_con_anterioridad', form);

  const calculateINPC = async () => {
    if (!selectedCargos.length || !selectedCargos[0]) return;

    const periodo = selectedCargos[0]?.periodo_fiscal?.periodo || '2024';
    const mes = selectedCargos[0]?.identificador_periodo;
    const _inpc = await getINPC(periodo, mes);
    setINPC(_inpc?.[0] ?? {});
  };

  const calcularActualizacion = async ({ importe }) => {
    try {
      const importeNumerico = typeof importe === 'number' ? importe : parseFloat(importe);

      if (Number.isNaN(importeNumerico)) {
        form.setFieldsValue({ actualizacion: 0 });
        return;
      }

      const actualizacion = await actualizacionTotal({
        importe_cargo: importeNumerico,
        tipo_de_cargo: selectedCargos[0],
        INPC,
      });
      form.setFieldsValue({ actualizacion });
    } catch (err) {
      _logger(err);
      form.setFieldsValue({ actualizacion: 0 });
    }
  };

  const calcularRecargos = async ({ importe }) => {
    try {
      const importeNumerico = typeof importe === 'number' ? importe : parseFloat(importe);
      const baseGravada = typeof _impuesto === 'number' ? _impuesto : parseFloat(_impuesto);

      if (Number.isNaN(importeNumerico) || Number.isNaN(baseGravada)) {
        form.setFieldsValue({ recargos: 0 });
        return;
      }

      const recargos = await recargosTotal({
        tipo_de_cargo: selectedCargos[0],
        importe_cargo: importeNumerico,
        base_gravada: baseGravada,
        fecha_de_vencimiento: selectedCargos[0]?.fecha_de_vencimiento,
      });

      form.setFieldsValue({ recargos });
    } catch (err) {
      _logger(err);
      form.setFieldsValue({ recargos: 0 });
    }
  };

  const calcularTotalDeContribuciones = () => {
    try {
      const valores = [
        _impuesto,
        _importeDeActualizacion,
        _importeRecargos,
        _importeMultas,
      ].map((valor) => {
        const valorNumerico = typeof valor === 'number' ? valor : parseFloat(valor);
        return Number.isNaN(valorNumerico) ? 0 : valorNumerico;
      });

      const total = valores.reduce((suma, valor) => suma + valor, 0);

      form.setFieldsValue({ total_de_contribuciones: round(total)[0] });
    } catch (error) {
      _logger(error);
      form.setFieldsValue({ total_de_contribuciones: 0 });
    }
  };

  const calcularCantidadACargo = () => {
    try {
      const totalContribuciones = typeof _importeTotalDeContribuciones === 'number'
        ? _importeTotalDeContribuciones
        : parseFloat(_importeTotalDeContribuciones) || 0;

      const montoPagado = typeof _importePagadoConAnterioridad === 'number'
        ? _importePagadoConAnterioridad
        : parseFloat(_importePagadoConAnterioridad) || 0;

      const diferencia = totalContribuciones - montoPagado;

      if (diferencia > 0) {
        form.setFieldsValue({
          cantidad_a_cargo: round(diferencia)[0],
          cantidad_a_favor: 0,
        });
      } else {
        form.setFieldsValue({
          cantidad_a_favor: round(Math.abs(diferencia))[0],
          cantidad_a_cargo: 0,
        });
      }
    } catch (error) {
      _logger(error);
      form.setFieldsValue({
        cantidad_a_cargo: 0,
        cantidad_a_favor: 0,
      });
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    await postRechazarDeclaracion({
      declaracion: declaracionId,
      canal_de_pago: 5,
    });
    setLoading(false);
  };

  const handleBeforeUnload = () => {
    handleCancel();
  };

  const handleUnload = (event) => {
    const message = 'Se perderán los datos capturados, ¿Estás seguro de salir?';
    event.preventDefault();
    // eslint-disable-next-line no-param-reassign
    event.returnValue = message; // Esto mostrará el cuadro de confirmación
    return message;
  };

  useEffect(() => {
    calcularActualizacion({ importe: _impuesto });
  }, [_impuesto]);

  useEffect(() => {
    calcularRecargos({ importe: _importeDeActualizacion });
  }, [_importeDeActualizacion, _impuesto]);

  useEffect(() => {
    calcularTotalDeContribuciones();
  }, [
    _impuesto,
    _importeDeActualizacion,
    _importeRecargos,
    _importeMultas,
  ]);

  useEffect(() => {
    calcularCantidadACargo();
  }, [
    _importeTotalDeContribuciones,
    _importePagadoConAnterioridad,
  ]);

  useEffect(() => {
    if (visible) {
      calculateINPC();
      form.resetFields();
    }
    window.addEventListener('unload', handleUnload);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('unload', handleUnload);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [visible]);

  const patchDeclaracionStatus = async (values) => {
    try {
      let statusId = null;

      const statusObject = {
        generada: statusDeclaracion.find((status) => !status.declaracion_presentada && !status.declaracion_en_ceros)?.id,
        en_ceros: statusDeclaracion.find((status) => status.declaracion_en_ceros)?.id,
        sin_importe: statusDeclaracion.find((status) => status.descripcion === 'Declaración con Cantidades a Pagar y a Favor en Cero')?.id || 0,
        saldo_favor: statusDeclaracion.find((status) => status.descripcion === 'Declaración con Cantidad a Favor')?.id || 0,
      };

      if (isZeroState) {
        statusId = statusObject.en_ceros;
      } else if (parseFloat(values.cantidad_a_favor) > 0) {
        statusId = statusObject.saldo_favor;
      } else if (parseFloat(values.cantidad_a_cargo) <= 0) {
        statusId = statusObject.sin_importe;
      } else {
        statusId = statusObject.generada;
      }
      const request = await patchGenerarDeclaracion({
        id: values.declaracion_empresarial,
        estatus_de_declaracion_empresarial: statusId,
        razones_para_presentar_en_ceros: values.razones_para_presentar_en_ceros,
      });

      if (!request) throw new Error('Error al actualizar la declaración.');
    } catch (error) {
      _logger(error);
    }
  };

  // Envia los datos del formulario.
  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      const _padronValues = {
        razon_social_nombre_completo:
        padron?.empresa?.contribuyente?.razon_social
        || padron?.empresa?.contribuyente?.nombre_completo
        || '',
        rfc: padron?.empresa?.contribuyente?.rfc || '',
        curp: padron?.empresa?.contribuyente?.curp || '',
        nombre: padron?.empresa?.contribuyente?.nombre_completo || '',
        nombre_completo: padron?.empresa?.contribuyente?.nombre_completo || '',
        apellido_paterno: padron?.empresa?.contribuyente?.apellido_paterno || '',
        apellido_materno: padron?.empresa?.contribuyente?.apellido_materno || '',
      };

      let _values = {
        ...values,
        ..._padronValues,
        declaracion_empresarial: declaracionId,
        entidad: 1,
        fecha_del_pago_realizado_anterior: isComplementaria && !isZeroState
          ? values.fecha_del_pago_realizado_anterior.format(DATE_FORMAT) : null,
      };

      if (isZeroState) {
        _values = {
          ..._values,
          cantidad_a_favor: 0,
          cantidad_a_cargo: 0,
          impuesto_determinado: 0,
          impuesto1_de_juegos_y_apuestas: 0,
          impuesto2_de_rifas_loterias_y_sorteos: 0,
          impuesto3_de_premios_obtenidos: 0,
          impuesto4_de_erogaciones_por_participar: 0,
          base1_de_juegos_y_apuestas: 0,
          base2_de_rifas_loterias_y_sorteos: 0,
          base3_de_premios_obtenidos: 0,
          base4_de_erogaciones_por_participar: 0,
          monto_pagado_con_anterioridad: 0,
          actualizacion: 0,
          multas: 0,
          recargos: 0,
          retenciones: 0,
          total_de_contribuciones: 0,
        };
      }

      if (!isComplementaria) {
        _values = {
          ..._values,
          fecha_del_pago_realizado_anterior: null,
          monto_pagado_con_anterioridad: 0,
        };
      }

      await patchDeclaracionStatus(_values);
      await postDeclaracionJuegos(_values);

      callback(_values.cantidad_a_cargo <= 0);
      setIsZeroState(false);
      setIsVisibleModal(false);
      setVisibleForm(false);
    } catch (error) {
      _logger(error);
    } finally {
      setLoading(false);
    }
  };

  const validacionesDeOperaciones = async (values) => {
    const retencion_rounded = round(parseFloat(values.retenciones) || 0)[0];
    const impuesto_rounded = round(parseFloat(values.impuesto) || 0)[0];
    const diferencia = parseFloat(impuesto_rounded) - parseFloat(retencion_rounded);

    if (diferencia < 0 && !isZeroState) {
      await form.validateFields(['impuesto_a_cargo', 'retenciones']);
      form.setFields([
        {
          name: 'impuesto_a_cargo',
          errors: ['El valor no puede ser mayor al impuesto correspondiente.'],
        },
        {
          name: 'retenciones',
          errors: ['El valor no puede ser mayor al impuesto correspondiente.'],
        },
      ]);
    } else if (diferencia >= 0 && !isZeroState) {
      await form.validateFields(['impuesto_a_cargo', 'retenciones']);
      form.setFields([
        {
          name: 'impuesto_a_cargo',
          errors: [],
        },
        {
          name: 'retenciones',
          errors: [],
        },
      ]);
    }
  };

  // Calcula el total de la cantidad a pagar.
  const total = () => {
    const values = form.getFieldsValue();
    const actualizacion = parseFloat(values.actualizacion) || 0;
    const recargos = parseFloat(values.recargos) || 0;
    const multas = parseFloat(values.multas) || 0;

    const impuesto1 = parseInt(values.impuesto1_de_juegos_y_apuestas, 10) || 0;
    const impuesto2 = parseInt(values.impuesto2_de_rifas_loterias_y_sorteos, 10) || 0;
    const impuesto3 = parseInt(values.impuesto3_de_premios_obtenidos, 10) || 0;
    const impuesto4 = parseInt(values.impuesto4_de_erogaciones_por_participar, 10) || 0;

    const impuesto_determinado_total = [
      impuesto1,
      impuesto2,
      impuesto3,
      impuesto4,
    ].reduce((a, b) => a + b, 0);

    const total_de_contribuciones = round([
      impuesto_determinado_total,
      actualizacion,
      recargos,
      multas,
    ].reduce((a, b) => parseFloat(a) + parseFloat(b), 0) || 0)[0];

    form.setFieldsValue({
      impuesto_determinado: impuesto_determinado_total,
      total_de_contribuciones,
    });

    validacionesDeOperaciones(values);
  };

  return (
    <>
      <Modal
        centered
        title={(
          <Row justify="space-between">
            <Text $accent>
              IMPUESTO SOBRE JUEGOS Y APUESTAS PERMITIDAS, SOBRE RIFAS, LOTERÍAS Y SORTEOS.
            </Text>
          </Row>
        )}
        visible={visible}
        closable={false}
        footer={(
          <Row justify="space-between" gutter={[10, 15]}>
            <Col xs={24} sm={24} md={12} style={{ textAlign: 'left' }}>
              <Text $accent>* Este campo es obligatorio</Text>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Button
                style={{ width: '100%', borderRadius: 5 }}
                onClick={() => callbackCancel()}
                loading={loading}
                $accent
                icon={<CloseOutlined />}
              >
                <Text>Cancelar</Text>
              </Button>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Button
                type="primary"
                style={{ width: '100%', borderRadius: 5 }}
                onClick={() => {
                  form.validateFields().then(() => {
                    setIsVisibleModal(true);
                  }).catch(() => {
                    setIsVisibleModal(false);
                  });
                }}
                loading={loading}
                icon={<SyncOutlined />}
                $accent
              >
                <Text>Generar</Text>
              </Button>
            </Col>
          </Row>
        )}
        width="90vw"
        style={{ maxWidth: '1200px' }}
      >
        <Spin spinning={loading} tip="Cargando...">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError
            initialValues={{
              fecha_del_pago_anterior: moment(),
              es_declaracion_en_ceros: false,
              impuesto1_de_juegos_y_apuestas: 0,
              impuesto2_de_rifas_loterias_y_sorteos: 0,
              impuesto3_de_premios_obtenidos: 0,
              impuesto4_de_erogaciones_por_participar: 0,
              base1_de_juegos_y_apuestas: 0,
              base2_de_rifas_loterias_y_sorteos: 0,
              base3_de_premios_obtenidos: 0,
              base4_de_erogaciones_por_participar: 0,
              actualizacion: 0,
              recargos: 0,
              multas: 0,
              cantidad_a_cargo: 0,
              cantidad_a_favor: 0,
              total_de_contribuciones: 0,
              impuesto_determinado: 0,
              monto_pagado_con_anterioridad: 0,
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12} lg={8}>
                <Form.Item
                  name="es_declaracion_en_ceros"
                  label="¿Es Declaración en ceros?"
                  required
                >
                  <Select
                    placeholder="Selecciona una opción"
                    trueFalse
                    allowClear={false}
                    onChange={(value) => setIsZeroState(value)}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              {isZeroState ? (
                <Col xs={24}>
                  <Form.Item
                    name="razones_para_presentar_en_ceros"
                    label="Razones por las que presenta en ceros"
                    rules={[_rules.required('El campo es requerido')]}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>
                </Col>
              ) : (
                <Col xs={24}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ minWidth: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Concepto</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            Juegos y apuestas permitidas; importe total de los billetes y demás
                            <br />
                            comprobantes que permitan participar en las apuestas que se crucen
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              tooltip="prueba"
                              name="base1_de_juegos_y_apuestas"
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.entero,
                                ..._rules.numeric,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                prefix="$"
                                onBlur={(val) => {
                                  const result = val.target.value * 0.04;
                                  form.setFieldsValue({ impuesto1_de_juegos_y_apuestas: parseInt(result, 10) });
                                  total();
                                }}
                                onChange={(value) => {
                                  if (value.target.value === '') {
                                    form.setFieldsValue({ impuesto1_de_juegos_y_apuestas: 0 });
                                  } else {
                                    const val_monto = value.target.value * 0.04;
                                    form.setFieldsValue({ impuesto1_de_juegos_y_apuestas: parseInt(val_monto, 10) });
                                  }
                                  total();
                                }}
                              />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Impuesto correspondiente</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="impuesto1_de_juegos_y_apuestas"
                              rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Rifas, loterías y sorteos; Importe total de los boletos vendidos</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="base2_de_rifas_loterias_y_sorteos"
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.entero,
                                ..._rules.numeric,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                prefix="$"
                                onBlur={(val) => {
                                  const result = val.target.value * 0.08;
                                  form.setFieldsValue({ impuesto2_de_rifas_loterias_y_sorteos: parseInt(result, 10) });
                                  total();
                                }}
                                onChange={(value) => {
                                  if (value.target.value === '') {
                                    form.setFieldsValue({ impuesto2_de_rifas_loterias_y_sorteos: 0 });
                                  } else {
                                    const val_monto = parseFloat(value.target.value) * 0.08;
                                    form.setFieldsValue({ impuesto2_de_rifas_loterias_y_sorteos: parseInt(val_monto, 10) });
                                  }
                                  total();
                                }}
                              />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Impuesto correspondiente</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="impuesto2_de_rifas_loterias_y_sorteos"
                              rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Monto de los premios obtenidos</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="base3_de_premios_obtenidos"
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.entero,
                                ..._rules.numeric,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                prefix="$"
                                onBlur={(val) => {
                                  const result = val.target.value * 0.06;
                                  form.setFieldsValue({ impuesto3_de_premios_obtenidos: parseInt(result, 10) });
                                  total();
                                }}
                                onChange={(value) => {
                                  if (value.target.value === '') {
                                    form.setFieldsValue({ impuesto3_de_premios_obtenidos: 0 });
                                  } else {
                                    const val_monto = parseFloat(value.target.value) * 0.06;
                                    form.setFieldsValue({ impuesto3_de_premios_obtenidos: parseInt(val_monto, 10) });
                                  }
                                  total();
                                }}
                              />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Impuesto correspondiente</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="impuesto3_de_premios_obtenidos"
                              rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            Erogaciones para participar en juegos con apuestas; importe total
                            <br />
                            de las cantidades que se entreguen por concepto de acceso con
                            <br />
                            y utilización de máquinas o instalaciones relacionadas
                            <br />
                            juegos de apuestas, cargas y recargas adicionales mediante
                            <br />
                            tarjetas,
                            bandas magnéticas, dispositivos electrónicos, etc.
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="base4_de_erogaciones_por_participar"
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.entero,
                                ..._rules.numeric,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                prefix="$"
                                onBlur={(val) => {
                                  const result = val.target.value * 0.10;
                                  form.setFieldsValue({ impuesto4_de_erogaciones_por_participar: parseInt(result, 10) });
                                  total();
                                }}
                                onChange={(value) => {
                                  if (value.target.value === '') {
                                    form.setFieldsValue({ impuesto4_de_erogaciones_por_participar: 0 });
                                  } else {
                                    const val_monto = parseFloat(value.target.value) * 0.10;
                                    form.setFieldsValue({ impuesto4_de_erogaciones_por_participar: parseInt(val_monto, 10) });
                                  }
                                  total();
                                }}
                              />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Impuesto correspondiente</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="impuesto4_de_erogaciones_por_participar"
                              rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={2} style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Text $accent $bold>PAGO DEL IMPUESTO</Text>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Impuesto determinado</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="impuesto_determinado"
                              id="impuesto_determinado"
                              rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Actualización (Monto manual)</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="actualizacion"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                prefix="$"
                              />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Recargos (Monto manual)</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="recargos"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                prefix="$"
                              />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Multas (Monto manual)</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="multas"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                prefix="$"
                                onChange={() => total()}
                              />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Total de contribuciones</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="total_de_contribuciones"
                              rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        {isComplementaria && (
                          <>
                            <tr>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Fecha del pago anterior</td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                <Form.Item
                                  name="fecha_del_pago_realizado_anterior"
                                  rules={[_rules.required('El campo es requerido')]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <DatePicker
                                    format={DATE_FORMAT}
                                    disabledDate={(d) => d.isAfter(toMoment(new Date()))}
                                    style={{ width: '100%' }}
                                  />
                                </Form.Item>
                              </td>
                            </tr>
                            <tr>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Monto pagado con anterioridad (Monto manual)</td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                <Form.Item
                                  hasFeedback
                                  name="monto_pagado_con_anterioridad"
                                  rules={[
                                    _rules.required('El valor tiene que ser 0 o mayor que 0'),
                                    ..._rules.numeric,
                                  ]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Input prefix="$" />
                                </Form.Item>
                              </td>
                            </tr>
                          </>
                        )}
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Cantidad a pagar</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              name="cantidad_a_cargo"
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Cantidad a favor</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              name="cantidad_a_favor"
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Col>
              )}
            </Row>
          </Form>
        </Spin>
      </Modal>
      <ModalConfirm
        visible={isVisibleModal}
        setVisible={setIsVisibleModal}
        onFinish={() => onFinish()}
        body="Al generar la declaración no podrá ser modificada. ¿Desea continuar?"
        btnText="Si, generar declaración"
        loading={loading}
      />
    </>
  );
};

FormJuegos.propTypes = {
  padron: propTypes.shape({
    empresa: propTypes.shape({
      contribuyente: propTypes.shape({
        razon_social: propTypes.string,
        rfc: propTypes.string,
        nombre_completo: propTypes.string,
        curp: propTypes.string,
        nombre: propTypes.string,
        apellido_paterno: propTypes.string,
        apellido_materno: propTypes.string,
        ciudadano: propTypes.shape({
          curp: propTypes.string,
          nombre: propTypes.string,
          apellido_paterno: propTypes.string,
          apellido_materno: propTypes.string,
        }),
      }),
    }),
  }).isRequired,
  statusDeclaracion: propTypes.number.isRequired,
  visible: propTypes.bool,
  isComplementaria: propTypes.bool,
  selectedCargos: propTypes.arrayOf({
    fecha_de_vencimiento: propTypes.string,
    importe: propTypes.number,
    identificador_periodo: propTypes.string,
    periodo_fiscal: propTypes.shape({
      periodo: propTypes.string,
    }),
  }),
  setVisibleForm: propTypes.func.isRequired,
  callback: propTypes.func.isRequired,
  callbackCancel: propTypes.func,
  declaracionId: propTypes.func.isRequired,
};

FormJuegos.defaultProps = {
  isComplementaria: false,
  selectedCargos: [],
  visible: false,
  callbackCancel: () => {},
};

export default FormJuegos;
